import { useContext, useState, useEffect, useRef } from "react";
import {
  Modal,
  Button,
  TextInput,
  Group,
  Radio,
  Select,
  Text,
  Stack,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { FoldersContext } from "../contexts/FoldersContext";
import { STANDARDS } from "../utils/constants";
import {
  IconCheck,
  IconChevronRight,
  IconCloudRain,
  IconDroplet,
  IconFolder,
} from "@tabler/icons-react";
import classes from "../styles/CreateFolderModal.module.css";
import { useRootFolderLabel } from "../hooks/useRootFolderLabel";

const CreateFolderModal = ({ opened, onClose, parent }) => {
  const { createFolder, rootId, flatFolders, setFolderToModify, folderIdMap } =
    useContext(FoldersContext);
  const [loading, setLoading] = useState(false);
  const [selectedParent, setSelectedParent] = useState(parent);
  const folderNameRef = useRef(null);
  const isRoot = selectedParent?.value === rootId;
  const rootFolderLabel = useRootFolderLabel();

  const form = useForm({
    initialValues: {
      folderName: "",
      isSewer: "true",
      standard: "",
    },
    validate: {
      folderName: (value) =>
        value.trim().length > 0 ? null : "Folder name is required",
      standard: (value) => (value ? null : "Standard is required"),
    },
  });

  useEffect(() => {
    if (opened) {
      form.reset();
      setSelectedParent(parent);
      setTimeout(() => {
        folderNameRef.current?.focus();
      }, 100);
    }
  }, [opened, parent]);

  const handleSubmit = async (values) => {
    setLoading(true);
    await createFolder({
      parentFolderId: isRoot ? rootId : selectedParent?.value,
      folderName: values.folderName,
      isSewer: values.isSewer === "true",
      standard: values.standard,
    });
    setLoading(false);
    onClose();
  };

  const handleParentChange = (value) => {
    const newParent = flatFolders.find(
      (folder) => String(folder.value) === value
    );
    setSelectedParent(newParent);
    setFolderToModify(newParent);
  };

  const folderOptions = flatFolders.map((folder) => ({
    value: String(folder.value),
    label: folder.label,
    path: folder.path,
  }));

  const renderStandardFlag = (value) => {
    if (!value) return null;
    const standard = STANDARDS.find((i) => i.value === value);
    if (!standard) return null;
    const { flag: Icon } = standard;
    return <Icon width={16} height={16} />;
  };

  return (
    <Modal
      keepMounted={false}
      opened={opened}
      onClose={onClose}
      title={
        <Group gap={3} align="center" justify="center">
          <Text fw={600} size="md">
            Create Folder
          </Text>
          <IconChevronRight size={18} />
          {isRoot ? (
            <Group gap={3} align="center">
              <Text size="sm">{rootFolderLabel}</Text>
            </Group>
          ) : (
            <Text size="sm">{selectedParent?.label}</Text>
          )}
        </Group>
      }
      centered
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Select
          label="Parent Folder"
          data={folderOptions}
          value={String(selectedParent?.value || "")}
          onChange={handleParentChange}
          placeholder="Select parent folder"
          searchable
          clearable={false}
          allowDeselect={false}
          required
          leftSection={<IconFolder size={16} />}
          renderOption={({ option, checked }) => {
            const folder = flatFolders.find(
              (f) => String(f.value) === option.value
            );
            const pathLabels =
              folder?.path?.map((id) => folderIdMap[id] || id) || [];
            const pathString = pathLabels.join(" / ");

            return (
              <Group align="center" gap="sm" wrap="nowrap">
                <IconFolder size={16} color="var(--mantine-color-gray-6)" />
                <Stack gap={2} style={{ flex: 1 }}>
                  <Text size="sm">{option.label}</Text>
                  {pathString && (
                    <Text size="xs" c="dimmed">
                      {pathString}
                    </Text>
                  )}
                </Stack>
                {checked && <IconCheck size={16} />}
              </Group>
            );
          }}
        />
        <TextInput
          ref={folderNameRef}
          label="Folder Name"
          {...form.getInputProps("folderName")}
          placeholder="Enter folder name"
          required
          mt="md"
        />
        <Select
          label="Standard"
          data={STANDARDS.map(({ value, label }) => ({ value, label })).filter(
            (option) => option.value && option.label
          )}
          {...form.getInputProps("standard")}
          mt="md"
          placeholder="Select a standard"
          clearable
          required
          leftSection={renderStandardFlag(form.values.standard)}
          renderOption={({ option, checked }) => {
            const standard = STANDARDS.find((i) => i.value === option.value);
            if (!standard) return null;
            const Icon = standard.flag;
            return (
              <Group flex="1" gap="xs">
                <Icon width={16} height={16} />
                <Text size="sm">{option.label}</Text>
                {checked && <IconCheck size={18} />}
              </Group>
            );
          }}
        />
        <Radio.Group
          label="Pipe Type"
          {...form.getInputProps("isSewer", { type: "radio" })}
          mt="md"
        >
          <Group gap="md" mt={5}>
            <Radio.Card
              radius="md"
              value="true"
              key="Sewer"
              className={classes.radioCard}
            >
              <Group align="flex-start" gap="sm" wrap="nowrap">
                <Radio.Indicator />
                <Stack gap="xs">
                  <Group gap="xs">
                    <IconDroplet size={16} />
                    <Text size="sm" fw={500}>
                      Sewer
                    </Text>
                  </Group>
                  <Text size="xs" c="dimmed">
                    Video files in this folder will be processed according to{" "}
                    {form.values.standard
                      ? STANDARDS.find((i) => i.value === form.values.standard)
                          ?.label
                      : "the selected"}{" "}
                    sewer standards
                  </Text>
                </Stack>
              </Group>
            </Radio.Card>
            <Radio.Card
              radius="md"
              value="false"
              key="Stormwater"
              className={classes.radioCard}
            >
              <Group align="flex-start" gap="sm" wrap="nowrap">
                <Radio.Indicator />
                <Stack gap="xs">
                  <Group gap="xs">
                    <IconCloudRain size={16} />
                    <Text size="sm" fw={500}>
                      Stormwater
                    </Text>
                  </Group>
                  <Text size="xs" c="dimmed">
                    Video files in this folder will be processed according to{" "}
                    {form.values.standard
                      ? STANDARDS.find((i) => i.value === form.values.standard)
                          ?.label
                      : "the selected"}{" "}
                    stormwater standards
                  </Text>
                </Stack>
              </Group>
            </Radio.Card>
          </Group>
        </Radio.Group>
        <Group mt="lg" justify="flex-end">
          <Button onClick={onClose} variant="subtle">
            Cancel
          </Button>
          <Button type="submit" loading={loading}>
            Create
          </Button>
        </Group>
      </form>
    </Modal>
  );
};

export default CreateFolderModal;
